import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { supabase } from './supabase';
import { logger } from './logger';

// ─── Types ───────────────────────────────────────────────────────────

export interface ParsedSkillGroup {
  name: string;
  code: string;
  skills: ParsedSkill[];
}

export interface ParsedPosition {
  name: string;
  code: string;
  pole: string;
  groups: ParsedSkillGroup[];
}

export interface ParsedSkill {
  name: string;
  code: string;
}

export interface ImportPreview {
  subsectorName: string;
  positions: ParsedPosition[];
  totalGroups: number;
  totalSkills: number;
}

export interface ImportResult {
  positionsCreated: number;
  groupsCreated: number;
  skillsCreated: number;
  errors: string[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

function slugify(text: string, maxLen = 90): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, maxLen);
}

// ─── Parsing ─────────────────────────────────────────────────────────

function parseRawText(text: string): ImportPreview {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const positions: ParsedPosition[] = [];

  let subsectorName = '';
  let currentPole = '';
  let currentPosition: ParsedPosition | null = null;
  let currentGroup: ParsedSkillGroup | null = null;
  let inSummarySection = true;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (!subsectorName && (line.startsWith('Hôtellerie') || line.startsWith('Hotellerie'))) {
      if (!line.includes('Secteur')) {
        subsectorName = line.trim();
        continue;
      }
    }

    const isPoleHeader = /^\d*\s*(ACCUEIL|H[ÉE]BERGEMENT|RESTAURATION|TECHNIQUE|S[ÉE]CURIT|ANIMATION|P[ÔO]LE|COMMERCE)/i.test(line) &&
      !line.includes('(H/F)') && line.length < 80;

    if (isPoleHeader) {
      currentPole = line.replace(/^\d+\s*/, '').trim();
      inSummarySection = false;
      continue;
    }

    const isPositionTitle = line.includes('(H/F)') && line.length < 120;

    if (isPositionTitle) {
      const name = line.replace(/\s*\(H\/F\)\s*/, '').trim();
      if (name.length < 3) continue;

      if (inSummarySection) continue;

      currentPosition = {
        name,
        code: slugify(name),
        pole: currentPole,
        groups: [],
      };
      positions.push(currentPosition);
      currentGroup = null;
      continue;
    }

    if (!currentPosition) continue;

    const groupMatch = line.match(/^(\d+)\.\s*(.+?)(?:\s{2,}|\t)(.+)/);
    const groupMatchSimple = line.match(/^(\d+)\.\s*([A-ZÀ-Ú][^.]+?)(?:\s+[A-ZÀ-Ú])/);

    if (groupMatch || groupMatchSimple) {
      let groupName: string;
      let skillsText: string;

      if (groupMatch) {
        groupName = groupMatch[2].trim();
        skillsText = groupMatch[3];
      } else {
        const dotIdx = line.indexOf('.', 2);
        const numPrefix = line.match(/^(\d+)\.\s*/);
        const afterNum = numPrefix ? line.slice(numPrefix[0].length) : line;

        const words = afterNum.split(/\.\s*/);
        if (words.length >= 2) {
          groupName = words[0].trim();
          skillsText = words.slice(1).join('. ');
        } else {
          groupName = afterNum.slice(0, 50);
          skillsText = afterNum.slice(50);
        }
      }

      if (!groupName || groupName.length < 3) continue;

      const groupCode = `${currentPosition.code}_g${currentPosition.groups.length + 1}`;
      currentGroup = {
        name: groupName,
        code: groupCode,
        skills: [],
      };
      currentPosition.groups.push(currentGroup);

      if (skillsText) {
        parseSkillsIntoGroup(skillsText, currentPosition.code, currentGroup);
      }
      continue;
    }

    if (currentGroup && line.length > 5 && !line.match(/^\d+\./)) {
      parseSkillsIntoGroup(line, currentPosition.code, currentGroup);
    }
  }

  const totalGroups = positions.reduce((s, p) => s + p.groups.length, 0);
  const totalSkills = positions.reduce((s, p) => s + p.groups.reduce((gs, g) => gs + g.skills.length, 0), 0);

  return { subsectorName, positions, totalGroups, totalSkills };
}

function parseSkillsIntoGroup(text: string, positionCode: string, group: ParsedSkillGroup) {
  const phrases = text.split(/\.\s*/).filter(s => s.trim().length > 4);
  for (const phrase of phrases) {
    const cleanPhrase = phrase.trim().replace(/^\s*[-•]\s*/, '');
    if (cleanPhrase.length < 5) continue;

    const skillCode = `${positionCode}_${slugify(cleanPhrase, 50)}`;
    group.skills.push({
      name: cleanPhrase.charAt(0).toUpperCase() + cleanPhrase.slice(1),
      code: skillCode,
    });
  }
}

// ─── File parsing entry points ───────────────────────────────────────

export async function parseDocxFile(file: File): Promise<ImportPreview> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return parseRawText(result.value);
}

export async function parseExcelFile(file: File): Promise<ImportPreview> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const text = XLSX.utils.sheet_to_txt(sheet);
  return parseRawText(text);
}

// ─── Execute import ──────────────────────────────────────────────────

export async function executeImport(
  preview: ImportPreview,
  sectorId: string,
  subsectorId?: string
): Promise<ImportResult> {
  const errors: string[] = [];
  let positionsCreated = 0;
  let groupsCreated = 0;
  let skillsCreated = 0;

  const positionIds: string[] = [];
  const groupIds: string[] = [];
  const skillIds: string[] = [];

  for (let posIdx = 0; posIdx < preview.positions.length; posIdx++) {
    const pos = preview.positions[posIdx];

    const posRow = {
      code: pos.code,
      name_fr: pos.name,
      sector_id: sectorId,
      subsector_id: subsectorId || null,
      category: pos.pole || null,
      sort_order: posIdx,
      active: true,
    };

    const { data: posData, error: posErr } = await supabase
      .from('positions')
      .upsert(posRow, { onConflict: 'code' })
      .select('id')
      .single();

    if (posErr) {
      errors.push(`Position "${pos.name}": ${posErr.message}`);
      continue;
    }
    positionsCreated++;
    const positionId = posData.id;
    positionIds.push(positionId);

    for (let grpIdx = 0; grpIdx < pos.groups.length; grpIdx++) {
      const grp = pos.groups[grpIdx];

      const grpRow = {
        code: grp.code,
        name_fr: grp.name,
        position_id: positionId,
        sort_order: grpIdx,
        active: true,
      };

      const { data: grpData, error: grpErr } = await supabase
        .from('skill_groups')
        .upsert(grpRow, { onConflict: 'code' })
        .select('id')
        .single();

      if (grpErr) {
        errors.push(`Group "${grp.name}": ${grpErr.message}`);
        continue;
      }
      groupsCreated++;
      const groupId = grpData.id;
      groupIds.push(groupId);

      for (let skIdx = 0; skIdx < grp.skills.length; skIdx++) {
        const sk = grp.skills[skIdx];

        const skRow = {
          code: sk.code,
          name_fr: sk.name,
          sector_id: sectorId,
          position_id: positionId,
          skill_group_id: groupId,
          sort_order: skIdx,
          active: true,
        };

        const { data: skData, error: skErr } = await supabase
          .from('skills')
          .upsert(skRow, { onConflict: 'code' })
          .select('id')
          .single();

        if (skErr) {
          errors.push(`Skill "${sk.name}": ${skErr.message}`);
        } else {
          skillsCreated++;
          if (skData) skillIds.push(skData.id);
        }
      }
    }
  }

  logger.functional('execute_import_v2', 'referentialImportService', {
    positions: positionsCreated,
    groups: groupsCreated,
    skills: skillsCreated,
    errors: errors.length,
  });

  // Recalcul de proximité après import (par lots, fire-and-forget : peut durer
  // plusieurs minutes sur un gros référentiel — relançable depuis la page dédiée)
  import('./referentialService').then(({ recomputeProximityChunked }) =>
    recomputeProximityChunked(0.8).then((pairsFound) => {
      logger.functional('compute_proximity_post_import', 'referentialImportService', { pairsFound });
    })
  ).catch((err) => {
    logger.error('compute_proximity_post_import', 'referentialImportService', { error: String(err) });
  });

  // Trigger batch translations (fire-and-forget, in parallel)
  const translateBatch = async (table: string, ids: string[]) => {
    const CHUNK = 50;
    for (let i = 0; i < ids.length; i += CHUNK) {
      try {
        await supabase.functions.invoke('translate-referential', {
          body: { table, ids: ids.slice(i, i + CHUNK) },
        });
      } catch (err) {
        logger.error('batch_translate', 'referentialImportService', { table, error: String(err) });
      }
    }
  };

  // Don't await - fire and forget so import returns quickly
  Promise.all([
    translateBatch('positions', positionIds),
    translateBatch('skill_groups', groupIds),
    translateBatch('skills', skillIds),
  ]).then(() => {
    logger.functional('batch_translation_complete', 'referentialImportService', {
      positions: positionIds.length,
      groups: groupIds.length,
      skills: skillIds.length,
    });
  }).catch(() => {});

  return { positionsCreated, groupsCreated, skillsCreated, errors };
}
